import { useCallback, useMemo, useState } from 'react';
import { Platform } from 'react-native';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../../store';
import { createApiClient, apiClient, getLocalTimestamp, getTimezoneOffsetString } from '../../../api/apiClient';
import { useToast } from '../../../components/LhToastContainer';
import { FormDataTransformed } from '../types';
import { setToastNotification } from '../../../store/slices/actionPlanSlice';
import { 
  mapApiSourceToOptionId, 
  mapApiTypeToOptionId, 
  mapApiPriorityToOptionId, 
  emailToDisplayName,
  getSourceDisplayText,
  getTypeDisplayText,
  getPriorityDisplayText,
} from '../utils/formOptions';
import { parseUTCDate } from '../utils/dateUtils';
import { handleActionPlanErrorWithFriendlyMessage, ACTION_PLAN_API_NAMES } from '../../../store/utils/actionPlanErrorHandler';

const transformApiToFormData = (actionPlanData: any): FormDataTransformed => {
  if (!actionPlanData) {
    return {
      title: '',
      source: null,
      type: null,
      priority: null,
      owner: null,
      assignee: null,
      startDate: null,
      targetDate: null,
      actionPlan: '',
      notes: '',
      frontlineInvolvement: false,
      employee: null,
      topOpportunity:false,
    };
  }
 
  return {
    title: actionPlanData.TITLE || '',
    source: actionPlanData.SOURCE
      ? { id: mapApiSourceToOptionId(actionPlanData.SOURCE), displayText: getSourceDisplayText(actionPlanData.SOURCE) }
      : null,
    type: actionPlanData.TYPE
      ? { id: mapApiTypeToOptionId(actionPlanData.TYPE), displayText: getTypeDisplayText(actionPlanData.TYPE) }
      : null,
    priority: actionPlanData.PRIORITY
      ? { id: mapApiPriorityToOptionId(actionPlanData.PRIORITY), displayText: getPriorityDisplayText(actionPlanData.PRIORITY) }
      : null,
    owner: actionPlanData.OWNER_NAME || actionPlanData.OWNER_EMAIL || actionPlanData.OWNER_GPID
      ? {
          id: actionPlanData.OWNER_GPID || '',
          displayText: actionPlanData.OWNER_NAME || emailToDisplayName(actionPlanData.OWNER_EMAIL || '') || actionPlanData.OWNER_GPID || '',
        }
      : null,
    assignee: actionPlanData.ASSIGNEE_NAME || actionPlanData.ASSIGNEE_EMAIL || actionPlanData.ASSIGNEE_GPID
      ? {
          id: actionPlanData.ASSIGNEE_GPID || '',
          displayText: actionPlanData.ASSIGNEE_NAME || emailToDisplayName(actionPlanData.ASSIGNEE_EMAIL || '') || actionPlanData.ASSIGNEE_GPID || '',
        }
      : null,
    startDate: parseUTCDate(actionPlanData.START_DATE),
    targetDate: parseUTCDate(actionPlanData.TARGET_DATE),
    actionPlan: actionPlanData.ACTION_PLAN_DESCRIPTION || '',
    notes: actionPlanData.NOTES || actionPlanData.DESCRIPTION || '',
    frontlineInvolvement: !!(actionPlanData.INVOLVED_EMPLOYEE_GPIDS || actionPlanData.INVOLVED_EMPLOYEE_NAMES),
    employee: (actionPlanData.INVOLVED_EMPLOYEE_GPIDS || actionPlanData.INVOLVED_EMPLOYEE_NAMES)
      ? {
          id: actionPlanData.INVOLVED_EMPLOYEE_GPIDS || '',
          displayText: actionPlanData.INVOLVED_EMPLOYEE_NAMES || actionPlanData.INVOLVED_EMPLOYEE_GPIDS || '',
        }
      : null,
    topOpportunity: actionPlanData.TOP_OPPORTUNITY === 1 || actionPlanData.TOP_OPPORTUNITY === true,
  };
};

export const useActionPlanDetails = (actionPlanData: any, actionPlanId?: string, onActionPlanUpdated?: (updatedData: any) => void, initialMode: 'create' | 'edit' | 'view' = 'view', showTopOpportunityToggle?: boolean, isTopOpportunity?: boolean, initialTopOpportunity?: boolean, initialArea?: string, selectedArea?: string, sessionId?: string) => {
  const [isEditMode, setIsEditMode] = useState(initialMode === 'edit');
  const [isDownloadingPDF, setIsDownloadingPDF] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const userGpid = useSelector(
    (state: RootState) =>
      state.appData?.userInfo?.GPID ?? state.auth?.user?.gpid ?? ""
  );

  // Redux selectors and dispatch
  const userEmail = useSelector(
    (state: RootState) => state.auth?.user?.username
  );
  // Ensure we include the current user's role id for RBAC validations on the API
  const userRoleId = useSelector((state: RootState) => state.appData?.userInfo?.roleId ?? state.auth?.user?.roleId ?? null);
  const dispatch = useDispatch<AppDispatch>();

  const { showToast } = useToast();

  const formData = useMemo(() => transformApiToFormData(actionPlanData), [actionPlanData]);

  const handleDownloadPDF = useCallback(async () => { 
    if (!actionPlanData?.ID) {
      console.error('Missing Action Plan ID');
      showToast({
        header: "PDF Download Error",
        subtext: "Action Plan ID not found",
        variant: "error",
        position: "top",
      });
      return;
    }

    try {
      setIsDownloadingPDF(true);

      const api = createApiClient();
      console.log('API client created');
      console.log('Base URL:', api.defaults.baseURL);

      // Make API call to get PDF as binary (use arraybuffer) and request PDF via Accept header
      console.log(`Making GET request to /action-plans/${actionPlanData.ID}/pdf`);
      console.log('Full URL:', `${api.defaults.baseURL}/action-plans/${actionPlanData.ID}/pdf`);
      
      const clientTimestamp = getLocalTimestamp();
      const response = await api.get(
        `/action-plans/${actionPlanData.ID}/pdf?clientTimestamp=${encodeURIComponent(clientTimestamp)}`,
        {
          responseType: 'arraybuffer',
          timeout: 30000, // 30 second timeout
          headers: {
            Accept: 'application/pdf',
          },
        }
      );

      console.log('API Response received');
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      // Detect content type and fail fast if server didn't return PDF
      const contentType = (response.headers['content-type'] || response.headers['Content-Type'] || '').toLowerCase();
      console.log('Response content-type:', contentType);

      // response.data is an ArrayBuffer when using arraybuffer responseType
      const arrayBuffer = response.data as ArrayBuffer;

      // If server returned HTML (or anything non-pdf) we still want to preview it
      if (!contentType.includes('pdf')) {
        try {
          // Decode arraybuffer to text for preview
          const decoder = new TextDecoder('utf-8');
          const preview = decoder.decode(new Uint8Array(arrayBuffer));
          console.error('Server returned non-PDF response (truncated preview):', preview.slice(0, 1000));

          // If it's HTML, attempt to automatically convert and download as PDF using html2pdf.js
          if (contentType.includes('html') || (preview.trim().startsWith('<!DOCTYPE') || preview.trim().startsWith('<html'))) {
            console.log('Handling HTML response fallback - attempting automatic HTML -> PDF conversion');

            // Prefer filename from Content-Disposition but default to .pdf
            const contentDisposition = response.headers['content-disposition'] || '';
            let filename = `action-plan-${actionPlanData.ID}.pdf`;
            if (contentDisposition.includes('filename')) {
              const filenameMatch = contentDisposition.match(/filename[^;=\n]*=(["']?)([^"';]*)\1/);
              if (filenameMatch && filenameMatch[2]) {
                // ensure .pdf extension
                filename = filenameMatch[2].replace(/\.html?$/i, '.pdf');
              }
            }

            // Create an offscreen iframe for the HTML so head/styles are respected
            const iframe = document.createElement('iframe');
            // Make iframe rendered but positioned off-screen so html2canvas can capture it
            iframe.style.position = 'absolute';
            iframe.style.left = '-12000px';
            iframe.style.top = '0';
            iframe.style.width = '800px'; // set width for PDF layout
            iframe.style.height = '1120px';
            iframe.style.border = 'none';
            // Keep iframe visible in layout (do not use visibility:hidden or opacity:0 as html2canvas may skip rendering)
            iframe.style.opacity = '1';
            iframe.style.pointerEvents = 'none';
            iframe.srcdoc = preview;
            document.body.appendChild(iframe);

            try {
              // Wait for iframe content to load (or timeout after 5s)
              await new Promise<void>((resolve) => {
                let resolved = false;
                const onLoad = () => {
                  if (resolved) return;
                  resolved = true;
                  iframe.removeEventListener('load', onLoad);
                  resolve();
                };
                iframe.addEventListener('load', onLoad);
                setTimeout(() => {
                  if (resolved) return;
                  resolved = true;
                  try { iframe.removeEventListener('load', onLoad); } catch(e){}
                  resolve();
                }, 5000);
              });

              // Ensure fonts and resources inside iframe have loaded
              const iframeDoc = iframe.contentDocument || (iframe.contentWindow && iframe.contentWindow.document);
              if (!iframeDoc) throw new Error('Unable to access iframe document for PDF conversion');

              // Debug: log body size and resource counts to help diagnose blank PDF
              try {
                console.log('iframe body innerHTML length:', iframeDoc.body?.innerHTML?.length || 0);
                console.log('iframe images count:', iframeDoc.images?.length || 0);
                console.log('iframe stylesheets count:', iframeDoc.styleSheets?.length || 0);
              } catch (dbgErr) {
                console.warn('Failed to read iframe debug info:', dbgErr);
              }

              try {
                // Wait for font faces to be ready if supported
                if (iframeDoc.fonts && typeof (iframeDoc.fonts as any).ready === 'object') {
                  await (iframeDoc.fonts as any).ready;
                }
              } catch (fontErr) {
                console.warn('Font readiness check failed or not supported in iframe:', fontErr);
              }

              // Small delay to let images/styles settle
              await new Promise((r) => setTimeout(r, 300));

              // Dynamically import html2pdf to avoid bundling when not needed
              const mod = await import('html2pdf.js');
              const html2pdf = (mod as any).default || (mod as any);

              // Configure options for better quality/format
              const opt = {
                margin:       10,
                filename:     filename,
                image:        { type: 'jpeg', quality: 0.98 },
                html2canvas:  { scale: 2, useCORS: true },
                jsPDF:        { unit: 'pt', format: 'a4', orientation: 'portrait' }
              };

            
              // Use the iframe's document body as source so head/styles are applied
              await html2pdf().from(iframeDoc.body).set(opt).save();

              showToast({
                header: 'PDF Downloaded',
                subtext: `PDF generated and downloaded as ${filename}`,
                variant: 'success',
                position: 'top',
              });

              

              // Cleanup
              try { document.body.removeChild(iframe); } catch (e) { /* ignore */ }
              return; // finished
            } catch (convErr) {
              console.error('Automatic HTML->PDF conversion failed:', convErr);

              // Remove iframe if conversion failed
              try { document.body.removeChild(iframe); } catch (e) { /* ignore */ }

              // Fallback: open HTML in new tab so user can Print->Save as PDF
              const htmlBlob = new Blob([preview], { type: 'text/html' });
              const url = URL.createObjectURL(htmlBlob);
              const newWindow = window.open(url, '_blank');
              if (!newWindow) {
                const link = document.createElement('a');
                link.href = url;
                link.download = `action-plan-${actionPlanData.ID}.html`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }

              showToast({
                header: 'HTML Report Opened',
                subtext: 'Unable to auto-generate PDF. Opened report in a new tab — use Print -> Save as PDF to create a PDF.',
                variant: 'warning',
                position: 'top',
              });

              setTimeout(() => URL.revokeObjectURL(url), 5000);
              return;
            }
          }

          // Try parse JSON
          try {
            const json = JSON.parse(preview);
            const parsedMessage = json?.message || JSON.stringify(json).slice(0, 300);

            showToast({
              header: 'PDF Download Failed',
              subtext: parsedMessage,
              variant: 'error',
              position: 'top',
            });

            throw new Error(`Server returned JSON error: ${parsedMessage}`);
          } catch (e) {
            showToast({
              header: 'PDF Download Failed',
              subtext: 'Server did not return a PDF. Preview shown in console.',
              variant: 'error',
              position: 'top',
            });

            throw new Error(`Server did not return a PDF. Preview: ${preview.slice(0,300)}`);
          }
        } catch (readErr) {
          console.error('Error while reading non-PDF response preview:', readErr);
          showToast({
            header: 'PDF Download Failed',
            subtext: 'Server did not return a PDF (unable to read response preview)',
            variant: 'error',
            position: 'top',
          });
          throw new Error('Server did not return a PDF (unable to read response preview)');
        }
      }

      // At this point content-type includes PDF - create blob from arraybuffer
      // React Native's Blob doesn't support ArrayBuffer directly, so convert to base64
      let blob: Blob;
      
      const isNative = Platform.OS === 'ios' || Platform.OS === 'android';
      
      if (isNative) {
        // React Native approach: convert ArrayBuffer to base64
        console.log(`Converting PDF to base64 for ${Platform.OS}`);
        
        const uint8Array = new Uint8Array(arrayBuffer);
        let binaryString = '';
        for (let i = 0; i < uint8Array.length; i++) {
          binaryString += String.fromCharCode(uint8Array[i]);
        }
        const base64String = btoa(binaryString);
        
        // For React Native, we'll use the base64 data URL directly
        const dataUrl = `data:application/pdf;base64,${base64String}`;
        
        // Create blob from data URL
        try {
          const response = await fetch(dataUrl);
          blob = await response.blob();
          console.log('React Native Blob created from base64 data URL');
        } catch (fetchErr) {
          console.error('Failed to create Blob from data URL:', fetchErr);
          // Fallback: create Blob directly from base64 string
          blob = new Blob([base64String], { type: 'application/pdf' });
          console.log('Fallback: Blob created from base64 string');
        }
      } else {
        // Web approach: directly use ArrayBuffer
        console.log('Creating Blob from ArrayBuffer (web environment)');
        blob = new Blob([arrayBuffer], { type: 'application/pdf' });
        console.log('Web Blob created from arraybuffer');
      }

      console.log('Blob created, size:', blob.size, 'bytes');

      if (blob.size === 0) {
        throw new Error('Received empty PDF from API');
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'] || '';
      let filename = `action-plan-${actionPlanData.ID}.pdf`;
      
      if (contentDisposition.includes('filename')) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=(["']?)([^"';]*)\1/);
        if (filenameMatch && filenameMatch[2]) {
          // Remove timestamp from filename (e.g., "action-plan-78-1764827896389.pdf" -> "action-plan-78.pdf")
          const rawFilename = filenameMatch[2];
          filename = rawFilename.replace(/-\d+\.pdf$/, '.pdf');
        }
      }

      console.log('Download filename:', filename);

      // Handle platform-specific download
      if (isNative) {
        // React Native: Save to device using FileSystem
        console.log(`Saving PDF to ${Platform.OS} device`);
        
        try {
          // Import FileSystem from expo or react-native-fs
          // For now, we'll use the standard approach with Linking
          const uint8Array = new Uint8Array(arrayBuffer);
          let binaryString = '';
          for (let i = 0; i < uint8Array.length; i++) {
            binaryString += String.fromCharCode(uint8Array[i]);
          }
          const base64String = btoa(binaryString);
          const fileUri = `data:application/pdf;base64,${base64String}`;
          
          // Open PDF viewer with the data URL
          // This will allow users to view and save the PDF using native sharing
          const { Linking } = require('react-native');
          
          showToast({
            header: "Success",
            subtext: `PDF prepared. Use your device's share/save options to save as ${filename}`,
            variant: "success",
            position: "top",
          });

          console.log('PDF data URL created for native viewer');
          
          // For iOS/Android, we can use Share API to let user save
          try {
            const { Share } = require('react-native');
            await Share.share({
              url: fileUri,
              title: filename,
              message: `Action Plan PDF: ${filename}`,
            });
          } catch (shareErr) {
            console.warn('Share failed, trying to open in viewer:', shareErr);
            // Fallback: just show success message, user can use their browser download
          }
          
          return;
        } catch (nativeErr) {
          console.error('Native PDF handling failed:', nativeErr);
          throw new Error('Failed to handle PDF on native device. Try using web version.');
        }
      }
      
      // Web environment: Use browser download
      console.log('Web environment detected - proceeding with browser download');

      // Check if we're in a browser environment
      if (typeof window === 'undefined' || typeof document === 'undefined') {
        console.log('Not in browser environment - window:', typeof window, 'document:', typeof document);
        throw new Error('This feature only works in a browser environment');
      }
      
      console.log('Browser environment confirmed - proceeding with download');

      // Create download link and trigger download
      const url = URL.createObjectURL(blob);
      console.log('Object URL created');
      
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      
      document.body.appendChild(link);
      console.log('Link appended to body');
      
      link.click();
      console.log('Link clicked - download triggered');
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        console.log('Cleanup completed');
      }, 100);

      showToast({
        header: "Success",
        subtext: `PDF downloaded successfully as ${filename}`,
        variant: "success",
        position: "top",
      });

      console.log('=== PDF Download Completed Successfully ===');
    } catch (error: any) {
      console.error("=== PDF Download Error ===:", error);
      console.error('Error stack:', error.stack);
      
      let errorMessage = "Failed to download PDF";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      console.error('Error message for user:', errorMessage);

      // Dispatch centralized ActionPlan error and show modal overlay for PDF download failures
      try {
        const centralizedError = handleActionPlanErrorWithFriendlyMessage(
          ACTION_PLAN_API_NAMES.DOWNLOAD_PDF,
          error,
          true,
          { actionPlanId: actionPlanData?.ID }
        );
        // Prefer friendly centralized message for toast fallback
        errorMessage = centralizedError?.message || errorMessage;
      } catch (handlerErr) {
        console.error('Failed to dispatch centralized ActionPlan error for PDF download:', handlerErr);
      }

      showToast({
        header: "PDF Download Failed",
        subtext: errorMessage,
        variant: "error",
        position: "top",
      });
    } finally {
      setIsDownloadingPDF(false);
      console.log('Setting isDownloadingPDF to false');
    }
  }, [actionPlanData?.ID, showToast]);

  const handleEditActionPlan = useCallback(() => {
    setIsEditMode(true);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setIsEditMode(false);
  }, []);

  const handleSaveEdit = useCallback(async (data: any) => {
    if (!actionPlanId  && !actionPlanData?.ID) {
      showToast({
        header: "Save Error",
        subtext: "Action Plan ID not found",
        variant: "error",
      });
      return;
    }

    setIsSaving(true);
    try {
      const api = createApiClient();
      const id = actionPlanData.ID;

      // Use the modified payload passed from form (already contains only changed fields)
      const putPayload: any = {
        ...data,
        updated_by: userGpid || 'system',
        updated_by_email: userEmail,
      };

      // Include role id for RBAC validation if available
      if (userRoleId) {
        putPayload.updated_by_role_id = userRoleId;
      }

      // Include is_temp and session_id when in standup mode
      if (sessionId) {
        const isTemp = actionPlanData?.IS_TEMP === 1;
        putPayload.is_temp = isTemp;
        putPayload.session_id = sessionId;
        
        // Only add clientTimestamp when is_temp is false
        if (!isTemp) {
          putPayload.clientTimestamp = getLocalTimestamp();
        }
      } else {
        // Not in standup mode, always add clientTimestamp
        putPayload.clientTimestamp = getLocalTimestamp();
      }

      if (data.assignee_name && !data.assignee_email) {
        putPayload.assignee_email = data.assignee_name?.email || '';
      }

      // Include top opportunity only when explicitly marked by the caller
      // (the StandUp form attaches __sendTopOpportunity and TOP_OPPORTUNITY when changed)
      if (data && (data.__sendTopOpportunity === true || data.TOP_OPPORTUNITY !== undefined)) {
        try {
          putPayload.top_opportunity = data.TOP_OPPORTUNITY ? true : false;
        } catch (e) {
          // fallback: ignore if value malformed
        }
        // remove internal marker so it isn't sent as-is
        delete putPayload.__sendTopOpportunity;
      }
      // Include area (capabilities) only when explicitly marked by the caller
      // (the StandUp form attaches __sendArea when capabilities were modified)
      if (data && (data.__sendArea === true || data.capabilities !== undefined)) {
        try {
          putPayload.capabilities = data.capabilities;
        } catch (e) {
          // ignore malformed capabilities
        }
        delete putPayload.__sendArea;
      }

      console.log('========================================');
      console.log('UPDATE ACTION PLAN - Request Details');
      console.log('========================================');
      console.log('Action Plan ID:', id);
      console.log('IS_TEMP:', actionPlanData?.IS_TEMP);
      console.log('Session ID received:', sessionId);
      console.log('PUT /action-plans payload:', JSON.stringify(putPayload, null, 2));
      console.log('========================================');

      // Make PUT API call to update the action plan with only modified fields
      const clientTimezone = getTimezoneOffsetString();
      const putResponse = await apiClient(
        api,
        `/action-plans/${id}?clientTimezone=${encodeURIComponent(clientTimezone)}`,
        'PUT',
        putPayload
      );

      console.log('Action Plan update response:', putResponse);

      if (putResponse?.status === 'success') {
        // Get the actionPlanId from the response
        const updatedActionPlanId = putResponse?.data?.actionPlanId || id;

        // Now call GET API to fetch the updated action plan details
        const clientTimestamp = getLocalTimestamp();
        const clientTimezone = getTimezoneOffsetString();
        
        // Build GET URL with is_temp=true if this was a temp action plan update
        let getUrl = `/action-plans/${updatedActionPlanId}?clientTimestamp=${encodeURIComponent(clientTimestamp)}&clientTimezone=${encodeURIComponent(clientTimezone)}`;
        if (sessionId && actionPlanData?.IS_TEMP === 1) {
          getUrl += `&is_temp=true`;
        }
        
        console.log('Fetching updated action plan with URL:', getUrl);
        
        const getResponse = await apiClient(
          api,
          getUrl,
          'GET'
        );

        console.log('Action Plan GET response:', getResponse);

        if (getResponse?.status === 'success' && getResponse?.data) {
          // Show success toast
          dispatch(setToastNotification({
            header: '',
            subtext: 'Action plan has been updated',
            variant: 'success',
            position: 'top',
            duration: 4000,
          }));

          // Call the callback to update the parent component with new data
          if (onActionPlanUpdated) {
            onActionPlanUpdated(getResponse.data);
          }

          setIsEditMode(false);
          return { success: true, data: getResponse.data };
        }
      }

      throw new Error(putResponse?.message || 'Failed to update action plan');
    } catch (error: any) {
      console.error("Error saving action plan:", error);
      let errorMessage = "Failed to save action plan";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Dispatch centralized ActionPlan error and show modal
      try {
        const centralizedError = handleActionPlanErrorWithFriendlyMessage(
          ACTION_PLAN_API_NAMES.UPDATE,
          error,
          true,
          { actionPlanId: actionPlanId || actionPlanData?.ID }
        );

        // Prefer friendly centralized message for user-facing toast
        errorMessage = centralizedError?.message || errorMessage;
      } catch (handlerErr) {
        console.error('Failed to dispatch centralized ActionPlan error for update:', handlerErr);
      }

      dispatch(setToastNotification({
        header: 'Error',
        subtext: errorMessage,
        variant: 'error',
        position: 'top',
        duration: 4000,
      }));

      return { success: false, error: errorMessage };
    } finally {
      setIsSaving(false);
    }
  }, [actionPlanId, actionPlanData, formData, showToast, dispatch, onActionPlanUpdated, userEmail, userRoleId]);

  return {
    isEditMode,
    formData,
    handleDownloadPDF,
    isDownloadingPDF,
    handleEditActionPlan,
    handleCancelEdit,
    handleSaveEdit,
    isSaving,
  };
};
